import React from 'react';
import { Mutation } from 'react-apollo';
import { bool, func, object } from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import compose from 'lodash/fp/compose';
import Grid from '@material-ui/core/Grid';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import { Formik, Form } from 'formik';
import { string as yupString, object as yupObject } from 'yup';

import Image from 'common/Image';
import {
	TextField,
	CountryFlagsSelect,
	CountriesList,
} from 'common/FormComponents';
import UpdateButton, { CancelButton } from 'common/ContactButtons';

import { withIntlProvider, intlShape } from 'modules/utils';
import { updateBusinessContactSettings } from 'modules/cp3-settings/card-integration/queries';

import { splitFullName } from 'utils/userHelpers';

import ModalDismiss from 'images/modal-dismiss-icon.svg';

import { locales } from './locales';

import {
	ContactInfoDialog,
	DialogWrapper,
	DialogTitle,
	DialogContent,
} from './styles';

const numberRegExp = new RegExp('.*[0-9].*[0-9].*');

const validationSchema = yupObject().shape({
	firstname: yupString()
		.trim()
		.required(<FormattedMessage id="errors.empty.firstname" />),
	lastname: yupString()
		.trim()
		.required(<FormattedMessage id="errors.empty.lastname" />),
	emailaddress: yupString()
		.trim()
		.required(<FormattedMessage id="errors.empty.emailaddress" />)
		.email(<FormattedMessage id="errors.invalid.emailaddress" />),
	country: yupString()
		.trim()
		.required(<FormattedMessage id="errors.empty.country" />),
	phonenumber: yupString()
		.trim()
		.required(<FormattedMessage id="errors.empty.phonenumber" />)
		.matches(
			numberRegExp,
			<FormattedMessage id="errors.invalid.phonenumber" />
		),
	streetaddress: yupString()
		.trim()
		.required(<FormattedMessage id="errors.empty.streetaddress" />),
	city: yupString()
		.trim()
		.required(<FormattedMessage id="errors.empty.city" />),
	stateprovince: yupString()
		.trim()
		.required(<FormattedMessage id="errors.empty.stateprovince" />),
	zippostalcode: yupString()
		.trim()
		.required(<FormattedMessage id="errors.empty.zippostalcode" />)
		.matches(
			numberRegExp,
			<FormattedMessage id="errors.invalid.zippostalcode" />
		),
});

const propTypes = {
	fullScreen: bool,
	open: bool,
	onClose: func,
	onCompleted: func,
	onCancel: func,
	onError: func,
	intl: intlShape,
	businessInfo: object,
	user: object,
};

const defaultProps = {
	businessInfo: {
		address: '',
		city: '',
		country: '',
		emailid: '',
		phone: '',
		primary_contact: '',
		state: '',
		zip: '',
	},
	user: {
		restaurantId: 0,
	},
};

const ContactInfoModal = ({
	fullScreen,
	open,
	businessInfo,
	user,
	onClose,
	onCompleted,
	onCancel,
	onError,
	intl,
}) => {
	const elementIdPrefix = 'contact-info-modal';
	const { firstName, lastName } = splitFullName(
		businessInfo.primary_contact || ''
	);

	return (
		<ContactInfoDialog
			scroll="body"
			fullScreen={fullScreen}
			open={open}
			onClose={onClose}
		>
			<div className="closer">
				<Image
					className="close-icon"
					onClick={onCancel}
					source={ModalDismiss}
					altText="Close"
				/>
			</div>
			<Mutation
				mutation={updateBusinessContactSettings}
				onCompleted={onCompleted}
				onCancel={onCancel}
				onError={onError}
			>
				{mutation => {
					const onUpdate = async (contactInfo, { setSubmitting }) => {
						setSubmitting(true);

						const { restaurantId } = user;
						const {
							firstname,
							lastname,
							emailaddress,
							country,
							phonenumber,
							streetaddress,
							city,
							stateprovince,
							zippostalcode,
						} = contactInfo;

						await mutation({
							variables: {
								input: {
									locationId: restaurantId || 0,
									action: 'updateBusinessContactSetting',
									businessPrimaryContact: `${firstname} ${lastname}`,
									businessEmailId: emailaddress,
									businessCountryCode: country,
									businessCountryName: CountriesList[country],
									businessPhone: phonenumber,
									businessAddress: streetaddress,
									businessCity: city,
									businessState: stateprovince,
									businessZip: zippostalcode,
								},
							},
						});

						setSubmitting(false);
					};
					return (
						<Formik
							initialValues={{
								firstname: firstName || '',
								lastname: lastName || '',
								emailaddress: businessInfo.emailid || '',
								phonenumber: businessInfo.phone || '',
								country: businessInfo.country || 'US',
								streetaddress: businessInfo.address || '',
								city: businessInfo.city || '',
								stateprovince: businessInfo.state || '',
								zippostalcode: businessInfo.zip || '',
							}}
							onSubmit={onUpdate}
							validationSchema={validationSchema}
						>
							{({
								values: {
									firstname,
									lastname,
									emailaddress,
									country,
									phonenumber,
									streetaddress,
									city,
									stateprovince,
									zippostalcode,
								},
								isValid,
								isSubmitting,
								touched,
								errors,
								handleSubmit,
								handleChange,
								handleBlur,
							}) => (
								<Form>
									<DialogWrapper>
										<DialogTitle>
											<h2 id={`${elementIdPrefix}-title`} className="title">
												<FormattedMessage id="title" />
											</h2>
											<span
												id={`${elementIdPrefix}-subtitle`}
												className="subtitle"
												// eslint-disable-next-line react/no-danger
												dangerouslySetInnerHTML={{
													__html: intl.formatMessage({ id: 'subtitle' }),
												}}
											/>
										</DialogTitle>
										<DialogContent>
											<Grid container spacing={24}>
												<Grid item xs={6}>
													<TextField
														label={intl.formatMessage({
															id: 'labels.firstname',
														})}
														error={errors.firstname}
														isValid={touched.firstname && !errors.firstname}
														value={firstname}
														name="firstname"
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														label={intl.formatMessage({
															id: 'labels.lastname',
														})}
														error={errors.lastname}
														isValid={touched.lastname && !errors.lastname}
														value={lastname}
														name="lastname"
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</Grid>
												<Grid item xs={12}>
													<TextField
														label={intl.formatMessage({
															id: 'labels.emailaddress',
														})}
														error={errors.emailaddress}
														isValid={
															touched.emailaddress && !errors.emailaddress
														}
														value={emailaddress}
														name="emailaddress"
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</Grid>
												<Grid item xs={12}>
													<CountryFlagsSelect
														label={intl.formatMessage({
															id: 'labels.country',
														})}
														value={country}
														name="country"
													/>
												</Grid>
												<Grid item xs={12}>
													<TextField
														label={intl.formatMessage({
															id: 'labels.phonenumber',
														})}
														error={errors.phonenumber}
														isValid={touched.phonenumber && !errors.phonenumber}
														value={phonenumber}
														name="phonenumber"
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</Grid>
												<Grid item xs={12}>
													<TextField
														label={intl.formatMessage({
															id: 'labels.streetaddress',
														})}
														error={errors.streetaddress}
														isValid={
															touched.streetaddress && !errors.streetaddress
														}
														value={streetaddress}
														name="streetaddress"
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</Grid>
												<Grid item xs={12}>
													<TextField
														label={intl.formatMessage({ id: 'labels.city' })}
														error={errors.city}
														isValid={touched.city && !errors.city}
														value={city}
														name="city"
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														label={intl.formatMessage({
															id: 'labels.stateprovince',
														})}
														error={errors.stateprovince}
														isValid={
															touched.stateprovince && !errors.stateprovince
														}
														value={stateprovince}
														name="stateprovince"
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														label={intl.formatMessage({
															id: 'labels.zippostalcode',
														})}
														error={errors.zippostalcode}
														isValid={
															touched.zippostalcode && !errors.zippostalcode
														}
														value={zippostalcode}
														name="zippostalcode"
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</Grid>
											</Grid>
										</DialogContent>
									</DialogWrapper>
									<div className="footer">
										<div className="actions">
											<CancelButton type="button" onClick={onCancel}>
												<FormattedMessage id="buttons.skip" />
											</CancelButton>
											<UpdateButton
												type="submit"
												disabled={!isValid || isSubmitting}
												onClick={handleSubmit}
											>
												<FormattedMessage id="buttons.update" />
											</UpdateButton>
										</div>
									</div>
								</Form>
							)}
						</Formik>
					);
				}}
			</Mutation>
		</ContactInfoDialog>
	);
};

ContactInfoModal.propTypes = propTypes;
ContactInfoModal.defaultProps = defaultProps;

export default compose(
	injectIntl,
	withIntlProvider(locales),
	withMobileDialog({ breakpoint: 'xs' })
)(ContactInfoModal);
